import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X, Save, Upload, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";

const imgSrc = (img) =>
  img && (img.startsWith("/api/") || img.startsWith("http")) ? mediaUrl(img) : img;

const IMG_FIELDS = [
  ["logo_tag", "Logo hang-tag (top left)", "400×730 · portrait, transparent PNG"],
  ["sewing", "Founders at sewing machine", "770×820 · ~1:1, transparent PNG"],
  ["jumpsuit", "Standing jumpsuit figure", "700×1340 · tall portrait, transparent PNG"],
  ["deadstock", "Deadstock fabric tags", "520×380 · landscape, transparent PNG"],
  ["walking", "Founders walking", "610×690 · portrait, transparent PNG"],
  ["thread", "Rescued thread ball", "480×320 · landscape, transparent PNG"],
];

function TextArea({ label, value, onChange, rows = 2, testId }) {
  return (
    <div>
      <label className="adm-label">{label}</label>
      <textarea rows={rows} className="adm-input resize-y" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function ListField({ title, items, onChange, testId }) {
  return (
    <div>
      <label className="adm-label">{title}</label>
      <div className="space-y-2">
        {(items || []).map((v, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="font-body text-[0.65rem] text-ink/40 mt-2.5 w-4 text-right">{i + 1}</span>
            <textarea rows={3} className="adm-input flex-1 resize-y" value={v} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`${testId}-${i}`} />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-ink/40 hover:text-destructive mt-2"><X size={15} /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...(items || []), ""])} className="mt-2 inline-flex items-center gap-1 font-body text-[0.6rem] uppercase tracking-wide2 text-ink/60 hover:text-ink" data-testid={`${testId}-add`}>
        <Plus size={12} /> Add paragraph
      </button>
    </div>
  );
}

function ImageField({ label, rec, value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const [nat, setNat] = useState(null);
  useEffect(() => {
    if (!value) { setNat(null); return; }
    const im = new window.Image();
    im.onload = () => setNat({ w: im.naturalWidth, h: im.naturalHeight });
    im.onerror = () => setNat(null);
    im.src = imgSrc(value);
  }, [value]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(`Uploaded — ${data.width || "?"}×${data.height || "?"}px`);
    } catch { toast.error("Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="flex gap-4 items-start">
      <div className="w-28 h-24 bg-[#e7d9bd] border border-ink/10 overflow-hidden shrink-0 flex items-center justify-center">
        {value ? <img src={imgSrc(value)} alt="" className="w-full h-full object-contain" /> : <span className="text-ink/30 text-xs">None</span>}
      </div>
      <div className="flex-1">
        <p className="font-body text-[0.72rem] text-ink/70">{label}</p>
        <p className="font-body text-[0.6rem] text-ink/50 mb-1.5">Recommended: {rec}{nat && ` · Uploaded: ${nat.w}×${nat.h}px`}</p>
        <label className="adm-btn-outline w-fit cursor-pointer">
          <Upload size={13} /> {busy ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testId}-file`} />
        </label>
        <input className="adm-input mt-2" placeholder="…or image URL" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-url`} />
      </div>
    </div>
  );
}

export default function AdminOurStory() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings/our_story").then(({ data }) => setF(data)); }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setImg = (k, v) => setF((s) => ({ ...s, images: { ...(s.images || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings/our_story", f); toast.success("Our Story saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  if (!f) return <p className="font-body text-sm text-ink/40">Loading…</p>;

  return (
    <div data-testid="admin-our-story" className="max-w-3xl">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl">Our Story Page</h1>
          <p className="font-body text-sm text-ink/50 mt-1">Edit every word and illustration of the storefront Our Story page.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/our-story" target="_blank" className="adm-btn-outline" data-testid="os-view-site"><ExternalLink size={13} /> View</Link>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-ink text-linen px-5 py-2.5 font-body text-[0.62rem] uppercase tracking-wide2 hover:bg-ink/90 disabled:opacity-50" data-testid="os-save">
            <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="space-y-8">
        <div className="adm-card space-y-4">
          <p className="font-heading text-xl">Header</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <TextArea label="Title (serif)" value={f.hero_title} rows={1} onChange={(v) => set("hero_title", v)} testId="os-f-hero-title" />
            <TextArea label="Script line" value={f.hero_script} rows={1} onChange={(v) => set("hero_script", v)} testId="os-f-hero-script" />
            <TextArea label="Tagline (caps)" value={f.tagline_caps} rows={1} onChange={(v) => set("tagline_caps", v)} testId="os-f-tagline-caps" />
            <TextArea label="Tagline (script)" value={f.tagline_script} rows={1} onChange={(v) => set("tagline_script", v)} testId="os-f-tagline-script" />
          </div>
          <ListField title="Intro paragraphs" items={f.intro} onChange={(v) => set("intro", v)} testId="os-f-intro" />
          <TextArea label="Intro script line" value={f.intro_script} onChange={(v) => set("intro_script", v)} testId="os-f-intro-script" />
          <TextArea label="Right note (script)" value={f.note1_script} onChange={(v) => set("note1_script", v)} testId="os-f-note1" />
        </div>

        <div className="adm-card space-y-4">
          <p className="font-heading text-xl">Section — From Waste to Voyage</p>
          <TextArea label="Section title" value={f.s1_title} rows={1} onChange={(v) => set("s1_title", v)} testId="os-f-s1-title" />
          <ListField title="Paragraphs" items={f.s1_body} onChange={(v) => set("s1_body", v)} testId="os-f-s1-body" />
          <TextArea label="Script line" value={f.s1_script} onChange={(v) => set("s1_script", v)} testId="os-f-s1-script" />
        </div>

        <div className="adm-card space-y-4">
          <p className="font-heading text-xl">Section — Built Together</p>
          <TextArea label="Section title" value={f.s2_title} rows={1} onChange={(v) => set("s2_title", v)} testId="os-f-s2-title" />
          <ListField title="Paragraphs" items={f.s2_body} onChange={(v) => set("s2_body", v)} testId="os-f-s2-body" />
        </div>

        <div className="adm-card space-y-4">
          <p className="font-heading text-xl">Footer band</p>
          <TextArea label="Left note (script)" value={f.note2_script} onChange={(v) => set("note2_script", v)} testId="os-f-note2" />
          <TextArea label="Closing note (script)" value={f.closing_script} rows={4} onChange={(v) => set("closing_script", v)} testId="os-f-closing" />
          <div>
            <label className="adm-label">Value pillars</label>
            <div className="space-y-2">
              {(f.values || []).map((v, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select className="adm-input w-32" value={v.icon} onChange={(e) => set("values", f.values.map((x, j) => j === i ? { ...x, icon: e.target.value } : x))} data-testid={`os-f-value-icon-${i}`}>
                    <option value="leaf">Leaf</option>
                    <option value="heart">Heart</option>
                    <option value="plane">Plane</option>
                  </select>
                  <input className="adm-input flex-1" value={v.label} onChange={(e) => set("values", f.values.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} data-testid={`os-f-value-label-${i}`} />
                  <button type="button" onClick={() => set("values", f.values.filter((_, j) => j !== i))} className="text-ink/40 hover:text-destructive"><X size={15} /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => set("values", [...(f.values || []), { icon: "leaf", label: "NEW VALUE" }])} className="mt-2 inline-flex items-center gap-1 font-body text-[0.6rem] uppercase tracking-wide2 text-ink/60 hover:text-ink"><Plus size={12} /> Add value</button>
          </div>
        </div>

        <div className="adm-card space-y-5">
          <p className="font-heading text-xl">Illustrations</p>
          {IMG_FIELDS.map(([k, label, rec]) => (
            <ImageField key={k} label={label} rec={rec} value={f.images?.[k]} onChange={(v) => setImg(k, v)} testId={`os-img-${k}`} />
          ))}
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-ink text-linen px-6 py-3 font-body text-[0.62rem] uppercase tracking-wide2 hover:bg-ink/90 disabled:opacity-50" data-testid="os-save-bottom">
            <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
